/**
 * Graph Data Adapter — Neo4j Migration Layer
 * Switches MaterialityOS + VoluntaryFW from MongoDB → Graph
 *
 * Graph API nests data: { iro: {...}, datapoint_count } and { dp: {...}, iro_name }
 * This adapter flattens + maps to existing frontend field names.
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch, useGraphWS } from "./api_service";

// ═══════════════════════════════════════════════════════════════
// SHAPE MAPPING: Graph → Frontend shorthand
// ═══════════════════════════════════════════════════════════════

function mapGraphIRO(raw) {
  // Graph returns { iro: {...scores...}, datapoint_count, esrs_count }
  // Flatten: merge iro fields with top-level counts
  const g = raw.iro ? { ...raw.iro, datapoint_count: raw.datapoint_count, esrs_count: raw.esrs_count, fw_count: raw.fw_count } : raw;

  // Graph uses impact_severity, financial_likelihood etc.
  const iSev = g.impact_severity ?? g.i_severity ?? g.iSev ?? 0;
  const iLik = g.impact_likelihood ?? g.i_likelihood ?? g.iLik ?? 0;
  const fMag = g.financial_magnitude ?? g.f_magnitude ?? g.fMag ?? 0;
  const fLik = g.financial_likelihood ?? g.f_likelihood ?? g.fLik ?? 0;

  return {
    _id: g.id || g._id,
    id: g.id || g._id,
    name: g.name,
    esrs_topic: g.esrs_topic || g.topic,
    pillar: g.pillar || (g.esrs_topic?.startsWith("E") ? "E" : g.esrs_topic?.startsWith("S") ? "S" : "G"),
    verdict: g.verdict || "not_material",
    rationale: g.rationale || "",

    // Scores — both snake_case and shorthand for compatibility
    iSev, iLik, fMag, fLik,
    impact_severity: iSev, impact_likelihood: iLik, financial_magnitude: fMag, financial_likelihood: fLik,
    iScore: iSev * iLik,
    fScore: fMag * fLik,
    combined: (iSev * iLik) + (fMag * fLik),

    // Graph enrichment — downstream counts
    dpCount: g.datapoint_count ?? 0,
    esrsCount: g.esrs_count ?? 0,
    fwCount: g.fw_count ?? 0,

    // Cascade detail
    activated_datapoints: g.activated_datapoints || [],
    triggered_esrs: g.triggered_esrs || [],
    triggered_fw_reqs: g.triggered_fw_reqs || [],

    // Metadata
    owner: g.owner || g.assigned_to || "",
    updated_at: g.updated_at || g.last_modified,
    source: "graph",
  };
}

function mapGraphDatapoint(raw) {
  // Graph returns { dp: {...}, iro_name, iro_topic, fw_count, evidence_count }
  const g = raw.dp ? { ...raw.dp, iro_name: raw.iro_name, iro_topic: raw.iro_topic, fw_count: raw.fw_count, evidence_count: raw.evidence_count } : raw;

  return {
    _id: g.id || g._id,
    id: g.id || g._id,
    name: g.name,
    status: g.status || "not_started",
    activation_status: g.activation_status || "dormant",
    activationStatus: g.activation_status || "dormant",
    value: g.value || g.current_value,
    methodology: g.methodology,
    unit: g.unit,
    quality: g.quality || g.quality_score,
    period: g.period,
    esrs_code: g.esrs_code,
    owner: g.owner,
    updated_at: g.updated_at,

    // Graph enrichment
    iro_name: g.iro_name,
    iro_topic: g.iro_topic,
    frameworkCount: g.fw_count ?? g.frameworks_satisfied?.length ?? 0,
    frameworks_satisfied: g.frameworks_satisfied || [],
    evidence_count: g.evidence_count || g.evidence_ids?.length || 0,
    evidence_ids: g.evidence_ids || [],
    iro_id: g.iro_id || g.linked_iro,
    esrs_topic: g.esrs_topic || g.iro_topic,

    source: "graph",
  };
}


// ═══════════════════════════════════════════════════════════════
// HOOK: useGraphIROs — Drop-in for MaterialityOS
// ═══════════════════════════════════════════════════════════════

export function useGraphIROs() {
  const [iros, setIros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const { lastEvent, connected } = useGraphWS();

  const fetchIROs = useCallback(async () => {
    setLoading(true);
    setError(null);

    // Try graph first (has 11 real IROs + downstream counts)
    const graphResult = await apiFetch("/graph/materiality/iros");

    if (!graphResult?._error && graphResult?.iros?.length > 0) {
      setIros(graphResult.iros.map(mapGraphIRO));
      setDataSource("graph");
      setLoading(false);
      return;
    }

    // Fallback to MongoDB
    const mongoResult = await apiFetch("/materiality/iros");
    if (!mongoResult?._error && mongoResult?.iros?.length > 0) {
      setIros(mongoResult.iros.map(iro => ({ ...mapGraphIRO(iro), source: "mongodb", dpCount: 0, esrsCount: 0, fwCount: 0 })));
      setDataSource("mongodb");
    } else if (graphResult?._error) {
      setError(graphResult);
    } else if (mongoResult?._error) {
      setError(mongoResult);
    } else {
      setIros([]);
      setDataSource("graph");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchIROs(); }, [fetchIROs]);

  useEffect(() => {
    if (lastEvent?.type === "materiality.verdict.changed" || lastEvent?.type === "cascade.completed") {
      fetchIROs();
    }
  }, [lastEvent, fetchIROs]);

  const scoreIRO = useCallback(async (iroId, scores) => {
    const result = await apiFetch(`/graph/materiality/iro/${iroId}`, {
      method: "POST",
      body: {
        impact_severity: scores.iSev ?? scores.impact_severity,
        impact_likelihood: scores.iLik ?? scores.impact_likelihood,
        financial_magnitude: scores.fMag ?? scores.financial_magnitude,
        financial_likelihood: scores.fLik ?? scores.financial_likelihood,
        verdict: scores.verdict,
        rationale: scores.rationale,
      },
    });
    if (!result?._error) {
      setIros(prev => prev.map(iro => iro.id === iroId ? mapGraphIRO({ iro: { ...iro, ...scores, ...result }, datapoint_count: iro.dpCount, esrs_count: iro.esrsCount }) : iro));
    }
    return result;
  }, []);

  const toggleVerdict = useCallback(async (iroId, newVerdict) => {
    return scoreIRO(iroId, { verdict: newVerdict });
  }, [scoreIRO]);

  const getCascade = useCallback(async (iroId) => {
    return apiFetch(`/graph/materiality/cascade/${iroId}`);
  }, []);

  const previewCascade = useCallback(async (iroId, proposedVerdict) => {
    return apiFetch("/graph/cascade-simulator/preview", {
      method: "POST",
      body: { iro_id: iroId, proposed_verdict: proposedVerdict },
    });
  }, []);

  const kpis = useMemo(() => {
    const material = iros.filter(i => i.verdict === "material").length;
    const totalDPs = iros.reduce((sum, i) => sum + (i.dpCount || 0), 0);
    const totalESRS = iros.reduce((sum, i) => sum + (i.esrsCount || 0), 0);
    const totalFW = iros.reduce((sum, i) => sum + (i.fwCount || 0), 0);
    return { total: iros.length, material, notMaterial: iros.length - material, totalDPs, totalESRS, totalFW };
  }, [iros]);

  return { iros, loading, error, dataSource, kpis, refetch: fetchIROs, scoreIRO, toggleVerdict, getCascade, previewCascade, wsConnected: connected };
}


// ═══════════════════════════════════════════════════════════════
// HOOK: useGraphDatapoints — Drop-in for VoluntaryFW
// ═══════════════════════════════════════════════════════════════

export function useGraphDatapoints(options = {}) {
  const { statusFilter, fwFilter, iroFilter } = options;
  const [datapoints, setDatapoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const { lastEvent, connected } = useGraphWS();

  const fetchDatapoints = useCallback(async () => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (statusFilter) params.set("status", statusFilter);
    if (fwFilter) params.set("framework", fwFilter);
    const qs = params.toString() ? `?${params.toString()}` : "";

    const graphResult = await apiFetch(`/graph/datapoints${qs}`);
    if (!graphResult?._error && graphResult?.datapoints) {
      setDatapoints(graphResult.datapoints.map(mapGraphDatapoint));
      setDataSource("graph");
    } else {
      const mongoResult = await apiFetch(`/voluntary/datapoints${qs}`);
      if (!mongoResult?._error && mongoResult?.datapoints) {
        setDatapoints(mongoResult.datapoints.map(dp => ({ ...mapGraphDatapoint(dp), source: "mongodb" })));
        setDataSource("mongodb");
      } else {
        setError(graphResult?._error ? graphResult : mongoResult);
      }
    }
    setLoading(false);
  }, [statusFilter, fwFilter]);

  const fetchReadiness = useCallback(async () => {
    const result = await apiFetch("/graph/export/readiness");
    if (!result?._error) setReadiness(result);
  }, []);

  useEffect(() => { fetchDatapoints(); fetchReadiness(); }, [fetchDatapoints, fetchReadiness]);

  useEffect(() => {
    if (lastEvent?.type === "cascade.completed" || lastEvent?.type === "datapoint.status.changed") {
      fetchDatapoints();
      fetchReadiness();
    }
  }, [lastEvent, fetchDatapoints, fetchReadiness]);

  const updateStatus = useCallback(async (dpId, newStatus, value, methodology) => {
    const body = { new_status: newStatus };
    if (value !== undefined) body.value = value;
    if (methodology) body.methodology = methodology;
    const result = await apiFetch(`/graph/datapoints/${dpId}/status`, { method: "PUT", body });
    if (!result?._error) {
      setDatapoints(prev => prev.map(dp => dp.id === dpId ? { ...dp, status: newStatus, ...(value !== undefined ? { value } : {}) } : dp));
      fetchReadiness();
    }
    return result;
  }, [fetchReadiness]);

  const linkEvidence = useCallback(async (dpId, evidenceId) => {
    return apiFetch(`/graph/datapoints/${dpId}/evidence`, { method: "POST", body: { evidence_id: evidenceId } });
  }, []);

  const getCrossMap = useCallback(async () => {
    return apiFetch("/graph/crossmap/matrix");
  }, []);

  const kpis = useMemo(() => {
    const total = datapoints.length;
    const approved = datapoints.filter(dp => dp.status === "approved").length;
    const required = datapoints.filter(dp => dp.activationStatus === "required").length;
    const dormant = datapoints.filter(dp => dp.activationStatus === "dormant").length;
    const withEvidence = datapoints.filter(dp => dp.evidence_count > 0).length;
    return { total, approved, required, dormant, withEvidence };
  }, [datapoints]);

  const filteredDatapoints = useMemo(() => {
    if (!iroFilter) return datapoints;
    return datapoints.filter(dp => dp.iro_id === iroFilter || dp.esrs_topic === iroFilter);
  }, [datapoints, iroFilter]);

  return { datapoints: filteredDatapoints, allDatapoints: datapoints, loading, error, dataSource, readiness, kpis, refetch: fetchDatapoints, updateStatus, linkEvidence, getCrossMap, wsConnected: connected };
}


// ═══════════════════════════════════════════════════════════════
// HOOK: useCSDDDScope — CSDDD stays on MongoDB, scope from graph
// ═══════════════════════════════════════════════════════════════

export function useCSDDDScope() {
  const [scope, setScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const { lastEvent } = useGraphWS();

  const fetchScope = useCallback(async () => {
    setLoading(true);
    const result = await apiFetch("/graph/csddd/scope");
    if (!result?._error) setScope(result);
    setLoading(false);
  }, []);

  useEffect(() => { fetchScope(); }, [fetchScope]);

  useEffect(() => {
    if (lastEvent?.type === "materiality.verdict.changed" || lastEvent?.type === "cascade.completed") {
      fetchScope();
    }
  }, [lastEvent, fetchScope]);

  return { scope, loading, refetch: fetchScope };
}


export const MIGRATION_STATUS = {
  "MaterialityOS.jsx": { status: "MIGRATED", primary: "/api/v1/graph/materiality/iros", fallback: "/api/v1/materiality/iros" },
  "VoluntaryFW.jsx": { status: "MIGRATED", primary: "/api/v1/graph/datapoints", fallback: "/api/v1/voluntary/datapoints" },
  "CSDDDPage.jsx": { status: "MONGODB (scope from graph)", enrichment: "/api/v1/graph/csddd/scope" },
  "GraphDashboard.jsx": { status: "NATIVE GRAPH", note: "Already graph-native" },
};
